import { AccountingEntryFormattedModal } from '@modules/Wallets/AccountingSync/components/display';
import type { AccountingEntrySource } from '@modules/Wallets/AccountingSync/lib/accountingEntryDisplay';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { WalletNavigationDropdown } from '@modules/Wallets/List/components/interaction';
import { CreateTopupButton } from '@modules/Wallets/Topups/components/interaction';
import { CreateTopupModal, CreateTopupModalRef } from '@modules/Wallets/Topups/components/modals';
import { CreateWithdrawalButton } from '@modules/Wallets/Withdrawals/components/interaction';
import { CreateWithdrawalModal, CreateWithdrawalModalRef } from '@modules/Wallets/Withdrawals/components/modals';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { WalletTransactionsAdvancedFilters, WalletTransactionsFilters } from '../../adapters/WalletApiAdapter';
import { useWallet, useWalletTransactions } from '../../hooks';
import { useWalletTransactionsColumns } from '../../hooks/useWalletTransactionsColumns';
import type { WalletTransaction } from '../../models/Wallet';
import { WalletAgGridTable, WalletBalance, WalletBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import {
  CreateManualTransactionButton,
  CreateWalletButton,
  ExportTransactionsButton,
  FiltersSection,
} from '../interaction';
import { CreateManualTransactionModal, type CreateManualTransactionModalRef } from '../modals';

const getColumnId = (columnId?: string, accessorKey?: string | number) => {
  if (columnId) return columnId;
  if (typeof accessorKey === 'string') return accessorKey;
  return String(accessorKey ?? '');
};

const matchesSearch = (transaction: WalletTransaction, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();

  const searchableValues = [
    transaction.id?.toString(),
    transaction.description,
    transaction.type,
    transaction.reference?.type,
    transaction.reference?.id?.toString(),
  ];

  return searchableValues.some((value) => value?.toString().toLowerCase().includes(normalized));
};

const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in']);
const VOID_OPERATORS = new Set(['is_null', 'is_not_null']);
const NUMERIC_COLUMNS = new Set(['amount', 'balance_total']);

const buildAdvancedFiltersPayload = (
  conditions: FilterConditionOutput[],
): WalletTransactionsAdvancedFilters | undefined => {
  const filters = conditions
    .map((condition) => {
      const { column, action } = condition;
      const rawValue = condition.value as unknown;

      if (!column || !action) {
        return null;
      }

      if (VOID_OPERATORS.has(action)) {
        return {
          column,
          operator: action,
        };
      }

      if (MULTI_VALUE_OPERATORS.has(action)) {
        let valuesArray: Array<string | number> = [];

        if (Array.isArray(rawValue)) {
          valuesArray = rawValue as Array<string | number>;
        } else if (typeof rawValue === 'string') {
          valuesArray = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          valuesArray = [rawValue as string | number];
        }

        if (!valuesArray.length) {
          return null;
        }

        const processedArray = NUMERIC_COLUMNS.has(column)
          ? valuesArray
              .map((item) => {
                const numericValue = typeof item === 'number' ? item : Number(item);
                return Number.isNaN(numericValue) ? null : numericValue;
              })
              .filter((item): item is number => item !== null)
          : valuesArray;

        if (!processedArray.length) {
          return null;
        }

        return {
          column,
          operator: action,
          value: processedArray,
        };
      }

      const singleValue = Array.isArray(rawValue) ? rawValue[0] : (rawValue as string | number | undefined);

      if (singleValue === undefined || singleValue === null || singleValue === '') {
        return null;
      }

      if (NUMERIC_COLUMNS.has(column)) {
        const numericValue = typeof singleValue === 'number' ? singleValue : Number(singleValue);
        if (Number.isNaN(numericValue)) {
          return null;
        }

        return {
          column,
          operator: action,
          value: numericValue,
        };
      }

      return {
        column,
        operator: action,
        value: singleValue,
      };
    })
    .filter((filter): filter is WalletTransactionsAdvancedFilters['filters'][number] => filter !== null);

  if (!filters.length) {
    return undefined;
  }

  return {
    filters,
    logic: 'AND',
  };
};

interface MainProps {
  walletId?: number;
  hideBreadcrumb?: boolean;
}

export const Main = ({ walletId: walletIdProp, hideBreadcrumb = false }: MainProps = {}) => {
  const { t } = useTranslation('walletsModule');
  const { canAccess } = usePermissions();
  const params = useParams<{ id: string }>();
  // 🎯 Si se pasa walletId como prop (caso external user), usarlo; si no, obtenerlo de los parámetros de URL
  // If walletId is passed as prop (external user case), use it; otherwise, get it from URL params
  const walletId = walletIdProp ?? Number(params.id);

  // Modales para crear recarga y retiro (solo tenant_external)
  const canCreateTopup = canAccess(['tenant_external'], ['topups.create']);
  const canCreateWithdrawal = canAccess(['tenant_external'], ['withdrawals.create']);
  const isExternal = canAccess(['tenant_external'], []);
  const canViewTransactions = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['wallet-transactions.view']);
  const canManualTransaction = canAccess(['tenant_3pl'], ['wallet-transactions.create']);
  const showWalletAccountingEntryColumn = canAccess(['tenant_3pl'], []);

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  const {
    data: walletDetail,
    isLoading: walletLoading,
    isError: walletError,
    error: walletErrorData,
    refetch: refetchWallet,
  } = useWallet(walletId);

  const searchValue = queryParams.search ?? '';
  const filterConditions = useMemo(
    () => (Array.isArray(queryParams.filters) ? queryParams.filters : []),
    [queryParams.filters],
  );

  const advancedFilters = useMemo(() => buildAdvancedFiltersPayload(filterConditions), [filterConditions]);

  const walletTransactionsFilters = useMemo<WalletTransactionsFilters>(
    () => ({
      sorting: { column: 'created_at', direction: 'desc' },
      ...(advancedFilters ? { advanced_filters: advancedFilters } : {}),
    }),
    [advancedFilters],
  );

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    isError: transactionsError,
    error: transactionsErrorData,
    refetch: refetchTransactions,
    isFetching: transactionsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWalletTransactions(walletId, walletTransactionsFilters);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [accountingEntryModalOpen, setAccountingEntryModalOpen] = useState(false);
  const [accountingEntryModalSource, setAccountingEntryModalSource] = useState<AccountingEntrySource | null>(null);
  const userCurrency = useCurrency();

  const handleOpenTransactionAccountingEntry = useCallback((transaction: WalletTransaction) => {
    if (!transaction.post_to_accounting) return;
    setAccountingEntryModalSource({
      accounting_entry: transaction.accounting_entry ?? null,
      accounting_entry_json: transaction.accounting_entry_json ?? null,
    });
    setAccountingEntryModalOpen(true);
  }, []);

  const { tanStackColumns, filteredColumnDefs } = useWalletTransactionsColumns({
    currency: walletDetail?.currency || userCurrency,
    selectedColumns,
    isTenant3pl: showWalletAccountingEntryColumn,
    onOpenAccountingEntry: showWalletAccountingEntryColumn ? handleOpenTransactionAccountingEntry : undefined,
  });
  const columnIds = useMemo(
    () =>
      tanStackColumns.map((column) =>
        getColumnId(
          column.id as string,
          Object.prototype.hasOwnProperty.call(column, 'accessorKey')
            ? (column as { accessorKey?: string | number }).accessorKey
            : undefined,
        ),
      ),
    [tanStackColumns],
  );

  useEffect(() => {
    if (selectedColumns.length === 0 && columnIds.length) {
      setSelectedColumns(columnIds);
    }
  }, [columnIds, selectedColumns.length]);

  const transactions = useMemo(() => transactionsData?.pages.flatMap((page) => page.data) ?? [], [transactionsData]);
  const filtersSignature = useMemo(() => JSON.stringify(filterConditions), [filterConditions]);

  const handleSearch = useCallback(
    (value: string) => {
      if (value === searchValue) {
        return;
      }
      updateQueryParams({ search: value });
    },
    [searchValue, updateQueryParams],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serializedNext = JSON.stringify(conditions);
      if (serializedNext === filtersSignature) {
        return;
      }
      updateQueryParams({ filters: conditions });
    },
    [filtersSignature, updateQueryParams],
  );

  const handleColumnsChange = useCallback((columnsIds: string[]) => {
    setSelectedColumns(columnsIds);
  }, []);

  const filteredTransactions = useMemo(
    () => transactions.filter((transaction) => matchesSearch(transaction, searchValue)),
    [transactions, searchValue],
  );

  const hasActiveFilters = Boolean(searchValue?.trim()) || filterConditions.length > 0;
  const totalResults = useMemo(() => {
    if (searchValue.trim()) {
      return filteredTransactions.length;
    }

    return transactionsData?.pages[0]?.meta?.total ?? filteredTransactions.length;
  }, [filteredTransactions.length, searchValue, transactionsData?.pages]);

  const isInitialTransactionsLoad = !transactionsData && transactionsLoading;
  const isRefetchingTransactions = transactionsFetching && !isInitialTransactionsLoad && !isFetchingNextPage;
  const shouldShowInitialLoading = walletLoading || isInitialTransactionsLoad;
  const isError = walletError || transactionsError;
  const error = walletErrorData ?? transactionsErrorData;

  const refetchAll = useCallback(() => {
    refetchWallet();
    refetchTransactions();
  }, [refetchTransactions, refetchWallet]);

  const createTopupModalRef = useRef<CreateTopupModalRef>(null);
  const createWithdrawalModalRef = useRef<CreateWithdrawalModalRef>(null);
  const createManualTransactionModalRef = useRef<CreateManualTransactionModalRef>(null);

  const handleOpenCreateTopup = useCallback(() => {
    if (!walletId) return;
    createTopupModalRef.current?.open(walletId);
  }, [walletId]);

  const handleOpenCreateWithdrawal = useCallback(() => {
    if (!walletId) return;
    createWithdrawalModalRef.current?.open(walletId);
  }, [walletId]);

  const handleTopupSuccess = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  const handleWithdrawalSuccess = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  const handleOpenManualTransaction = useCallback(() => {
    if (!walletId) return;
    createManualTransactionModalRef.current?.open(walletId);
  }, [walletId]);

  const handleManualTransactionSuccess = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  // 🎯 Solo mostrar breadcrumb si no está oculto (para usuarios external no se muestra)
  // Only show breadcrumb if not hidden (for external users it's not shown)
  const breadcrumb = hideBreadcrumb ? null : (
    <WalletBreadcrumb walletId={walletId} walletDetail={walletDetail ?? null} />
  );

  if (shouldShowInitialLoading) {
    return (
      <>
        {breadcrumb}
        <LoadingState message={t('loading.walletDetail')} />
      </>
    );
  }

  if (isError || !walletDetail) {
    return (
      <>
        {breadcrumb}
        <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetchAll} title={t('errors.title')} />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="px-6 pt-4 flex flex-col gap-4">
        {/* Controles y acciones principales - Main controls and actions */}
        <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
          {isExternal && (
            <>
              {walletId && (
                <>
                  {canCreateTopup && <CreateTopupButton onClick={handleOpenCreateTopup} />}
                  {canCreateWithdrawal && <CreateWithdrawalButton onClick={handleOpenCreateWithdrawal} />}
                </>
              )}
              <WalletNavigationDropdown />
            </>
          )}
          {canManualTransaction && walletId && <CreateManualTransactionButton onClick={handleOpenManualTransaction} />}
          <CreateWalletButton />
        </div>

        {/* Saldo disponible - Available balance */}
        <WalletBalance walletDetail={walletDetail} />

        {canViewTransactions && (
          <>
            <div className="mb-2">
              <div className="relative flex items-center justify-between flex-wrap gap-3">
                <FiltersSection
                  columns={tanStackColumns}
                  selectedColumns={selectedColumns}
                  onColumnsChange={handleColumnsChange}
                  searchValue={searchValue}
                  onSearch={handleSearch}
                  filterConditions={filterConditions}
                  onFilterChange={handleFilterChange}
                  actions={
                    <ExportTransactionsButton
                      walletId={walletId}
                      advancedFilters={advancedFilters}
                      sorting={walletTransactionsFilters.sorting}
                      disabled={filteredTransactions.length === 0}
                    />
                  }
                />

                <div className="flex flex-col items-end ml-auto">
                  <TotalResults total={totalResults} />
                </div>
              </div>
            </div>

            {/* Tabla de transacciones: AgGrid siempre montado (evita removeChild / conflictos DOM con ag-grid) */}
            <div
              className={`transition-opacity duration-300 ${isRefetchingTransactions ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
            >
              <WalletAgGridTable
                transactions={filteredTransactions}
                columnDefs={filteredColumnDefs}
                fetchNextPage={() => {
                  void fetchNextPage();
                }}
                hasNextPage={Boolean(hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
            {filteredTransactions.length === 0 && (
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
                title={t('emptyStateTransactions')}
              />
            )}
          </>
        )}
      </div>

      {/* Modales para crear recarga y retiro */}
      {isExternal && walletId && (
        <>
          <CreateTopupModal ref={createTopupModalRef} onSuccess={handleTopupSuccess} />
          <CreateWithdrawalModal ref={createWithdrawalModalRef} onSuccess={handleWithdrawalSuccess} />
        </>
      )}
      {canManualTransaction && walletId && (
        <CreateManualTransactionModal
          ref={createManualTransactionModalRef}
          onSuccess={handleManualTransactionSuccess}
        />
      )}

      {showWalletAccountingEntryColumn && (
        <AccountingEntryFormattedModal
          open={accountingEntryModalOpen}
          onOpenChange={(open) => {
            setAccountingEntryModalOpen(open);
            if (!open) setAccountingEntryModalSource(null);
          }}
          source={accountingEntryModalSource}
        />
      )}
    </>
  );
};
